import {useCallback, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import dayjs from "dayjs";
import {uniqBy, isEmpty, isNumber} from "es-toolkit/compat";

import {ORDER_LAB_STATUS} from "@/constants/orderLabStatus";
import {useResults} from "@/hooks/useResults/useResults";
import {base64Encrypt} from "@/helpers/base64Encrypt";
import {loadClientSecret, requestChangeAppointmentType, requestReplacementKit, getOrderById} from "@/features/orders";
import {selectKitBaseCost} from "@/features/appSettings/appSettingsSelectors";
import {selectUserDetails} from "@/features/user/userSelectors";
import {selectBiomarkersData} from "@/features/biomarkers/biomarkersSelectors";
import {selectOrdersData, selectOrdersStatus} from "@/features/orders/ordersSelector";
import {selectPartnersDetails} from "@/features/partners/partnersSelectors";
import {selectResultsHealthCategories} from "@/features/results/resultsSelectors";
import {selectSubscriptionsData} from "@/features/subscriptions/subscriptionsSelectors";
import {selectPlusTests, selectTestData} from "@/features/tests/testsSelectors";
import {config} from "@/config";

export const useOrders = (options = {}) => {
  const {orderId, amount, orderReference} = options;
  const orders = useSelector(selectOrdersData);
  const {isFetching = false} = useSelector(selectOrdersStatus);
  const testList = useSelector(selectTestData);
  const plusTests = useSelector(selectPlusTests);
  const subscriptions = useSelector(selectSubscriptionsData);
  const partnerId = useSelector(selectPartnersDetails)?.partnerId || 0;
  const userDetails = useSelector(selectUserDetails);
  const healthCategories = useSelector(selectResultsHealthCategories);
  const kitBaseCost = useSelector(selectKitBaseCost);
  const biomarkerState = useSelector(selectBiomarkersData);

  const [clientSecret, setClientSecret] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const {latestResult, getResultByOrderId} = useResults();

  const dispatch = useDispatch();

  const orderFiltered = useMemo(() => {
    if (isEmpty(orders) || !orderId) return {};
    let orderFound = orders?.find(order => order.orderId == orderId);
    const isTruCheck = Boolean(orderFound?.appointmentRequired == 1 && orderFound?.testType != 5);

    if (!isEmpty(orderFound)) {
      orderFound = {...orderFound, isTruCheck: isTruCheck};
    }

    return orderFound || {};
  }, [orders, orderId]);

  let websiteURL;
  if (config.websiteId == 1) {
    websiteURL = "forthwithlife";
  } else {
    websiteURL = "sportsbloodtests";
  }

  const isForth = config.websiteId === 1;
  const isEdge = config.websiteId === 2;
  const isBiomarkerTracking = !isForth && !isEdge;

  const getFemaleOrders = useCallback(() => {
    const femaleOrders = orders?.filter(order => order.isFemaleHormoneTest == 1) || [];

    if (!isEmpty(femaleOrders)) {
      return femaleOrders.sort((a, b) => a.orderReference.localeCompare(b.orderReference));
    }

    //* Use the if statement below to group the female orders by order reference and remove the if statement above
    // if (femaleOrders) {
    //   const groupedMyFormOrders = [...femaleOrders]
    //     .sort((a, b) => a.orderId - b.orderId)
    //     .reduce((acc, curr) => {
    //       const {orderReference} = curr;
    //       const [orderRef, kit] = orderReference.split("-");

    //       const orderFound = acc.find(order => order.orderReference.includes(orderRef));

    //       if (orderFound) {
    //         return [
    //           ...acc.filter(order => order.orderId != orderFound.orderId),
    //           {
    //             ...orderFound,
    //             orderReference: orderFound.orderReference.includes(kit)
    //               ? orderFound.orderReference
    //               : `${orderFound.orderReference}/${kit}`,
    //           },
    //         ];
    //       } else {
    //         return [...acc, curr];
    //       }
    //     }, []);

    //   return groupedMyFormOrders;
    // }

    return [];
  }, [orders]);

  const checkOrderIdExist = useCallback(
    orderId => {
      return orders.some(order => order?.orderId == orderId);
    },
    [orders],
  );

  const isValidOrderId = useCallback(orderId => {
    if (!orderId) return null;

    const orderIdNumber = Number(orderId);
    if (isNaN(orderIdNumber)) return false;
    if (!isNumber(orderIdNumber)) return false;

    return true;
  }, []);

  const getOrderStatusCopy = useCallback(orderLabStatus => {
    if (!orderLabStatus) return;

    const isOrderDispatched = ORDER_LAB_STATUS.dispatching.some(status => status == orderLabStatus);
    const isOrderPosted = ORDER_LAB_STATUS.posted.some(status => status == orderLabStatus);
    const isOrderAtTheLab = ORDER_LAB_STATUS.atTheLab.some(status => status == orderLabStatus);
    const isOrderBeingReviewed = ORDER_LAB_STATUS.beingReviewed.some(status => status == orderLabStatus);
    const isOrderPartnerReview = ORDER_LAB_STATUS.partnerReview.some(status => status == orderLabStatus);
    const isOrderOnHold = ORDER_LAB_STATUS.onHold.some(status => status == orderLabStatus);
    const isOrderHold = ORDER_LAB_STATUS.hold.some(status => status == orderLabStatus);
    const isAwaitingAppointment = ORDER_LAB_STATUS.appointmentBookingRequired.some(status => status == orderLabStatus);

    return {
      isOrderDispatched,
      isOrderPosted,
      isOrderAtTheLab,
      isOrderBeingReviewed,
      isOrderPartnerReview,
      isOrderOnHold,
      isOrderHold,
      isAwaitingAppointment,
    };
  }, []);

  const getOrdersByLabStatus = useCallback(
    (orderLabStatus = []) => {
      if (!orderLabStatus || !Array.isArray(orderLabStatus)) return;

      const ordersFiltered =
        orders?.filter(order => orderLabStatus.some(status => status == order.orderLabStatus)) || [];

      if (!isEmpty(ordersFiltered)) {
        return ordersFiltered.sort((a, b) => parseInt(a.orderId) - parseInt(b.orderId));
      }

      return [];
    },
    [orders],
  );

  const getCurrentKitId = useCallback(order => {
    if (!order) return;

    if (!order.phlebotomy) {
      return "1";
    }

    if (order.postPKit === "Appointment") {
      return "2";
    }

    if (order.postPKit === "Partner Clinic") {
      return "5";
    }

    if (order.postPKit === "Kit Only") {
      return "3";
    }
  }, []);

  const getSameTest = useCallback(
    (params = {}) => {
      const {orderId} = params;
      const result = getResultByOrderId({orderId});
      const isAntibodyTest = result?.antibodyResultText !== "";

      let test = {};
      if (result?.testId && !isAntibodyTest) {
        test = testList?.filter(test => test?.testId == result.testId)?.[0] || {};
      }

      const testIcon = result?.bespokeTest == 1 ? "bespoke-test-icon" : test?.testIcon;

      let nativeAppExternalUrl = "";
      if (window.config.isNativeApp) {
        nativeAppExternalUrl += "?external=true";
      }

      if (!isEmpty(test)) {
        const healthCategoriesList = getHealthCategories(test?.biomarkers?.map(biomarker => biomarker?.biomarkerId));
        test = {
          ...test,
          isSameTest: 1,
          testIcon,
          healthCategories: healthCategoriesList || [],
          biomarkers: test?.biomarkers.map(biomarker => {
            const {
              biomarkerInfo_life_summary: biomarkerInfoLife = "",
              biomarkerInfo_edge_summary: biomarkerInfoEdge = "",
            } = biomarkerState.find(marker => marker?.biomarkerId == biomarker?.biomarkerId) || {};

            return {
              ...biomarker,
              biomarkerDescription: isEdge && biomarkerInfoEdge ? biomarkerInfoEdge : biomarkerInfoLife,
            };
          }),
          url: `https://shop.${websiteURL}.co.uk/checkout/${base64Encrypt(test.testId + ":0:1:" + partnerId)}${nativeAppExternalUrl}`,
        };
      }

      if (isEmpty(test) && !isAntibodyTest) {
        const healthCategoriesList = getHealthCategories(result?.biomarkers?.map(biomarker => biomarker?.biomarkerId));
        test = {
          ...test,
          ...result,
          isSameTest: 1,
          testIcon,
          healthCategories: healthCategoriesList || [],
          biomarkers: result?.biomarkers.map(biomarker => {
            const {
              biomarkerInfo_life_summary: biomarkerInfoLife = "",
              biomarkerInfo_edge_summary: biomarkerInfoEdge = "",
            } = biomarkerState.find(marker => marker?.biomarkerId == biomarker?.biomarkerId) || {};

            return {
              ...biomarker,
              biomarkerDescription: isEdge && biomarkerInfoEdge ? biomarkerInfoEdge : biomarkerInfoLife,
            };
          }),
          url: `https://shop.${websiteURL}.co.uk/checkout/${base64Encrypt(result?.testId + ":0:1:" + partnerId)}${nativeAppExternalUrl}`,
        };
      }

      return test;
    },
    [biomarkerState, getResultByOrderId, healthCategories, partnerId, testList, websiteURL],
  );

  const getBespokeTestFromHealthCategories = useCallback(() => {
    if (isEmpty(userDetails?.healthCategoriesPreferences) || isEmpty(plusTests)) return {};

    let healthCategoriesPlusTest =
      userDetails?.healthCategoriesPreferences
        ?.map(categoryId => {
          const plusTestId = healthCategories.filter(healthCategory => healthCategory?.id == categoryId)?.[0]
            ?.plusTestId;
          return plusTestId ? Number(plusTestId) : 0;
        })
        .filter(plusTestId => plusTestId) || [];

    healthCategoriesPlusTest = uniqBy(healthCategoriesPlusTest, plusTestId => plusTestId);

    let testPrice = 0;

    healthCategoriesPlusTest?.forEach(plusTestId => {
      const plusTest = plusTests?.find(plusTest => plusTest?.testId == plusTestId) || {};

      if (!isEmpty(plusTest)) {
        testPrice = testPrice + parseFloat(plusTest?.testPrice) || 0;
      }
    });

    const testHealthCategories =
      healthCategories.filter(healthCategory =>
        userDetails?.healthCategoriesPreferences?.some(categoryId => categoryId == healthCategory.id),
      ) || [];

    const healthCategoryBiomarkers =
      testHealthCategories
        .map(healthCategory => healthCategory?.biomarkerIds)
        .flat()
        .map(biomarkerId => {
          const biomarker = biomarkerState.filter(marker => marker.biomarkerId == biomarkerId)?.[0] || {};
          return biomarker;
        })
        .sort((a, b) => {
          return a?.biomarkerName?.trim()?.localeCompare(b?.biomarkerName?.trim());
        }) || [];

    if (isEmpty(healthCategoriesPlusTest)) return {};

    let nativeAppExternalUrl = "";
    if (window.config.isNativeApp) {
      nativeAppExternalUrl += "?external=true";
    }

    return {
      testName: `${userDetails.customerFirstName}'s bespoke test ${dayjs().format("MM/YYYY")}`,
      biomarkers: uniqBy(healthCategoryBiomarkers, "biomarkerId"),
      isSubscriptionTest: 0,
      isBespokeOutRange: 0,
      isBespokeHealthCategories: 1,
      healthCategories: uniqBy(testHealthCategories, "id") || [],
      testIcon: "bespoke-test-icon",
      testPrice: (testPrice + kitBaseCost).toString(),
      plusTests: healthCategoriesPlusTest,
      url: window.config.isDev
        ? `https://devbuild.${window.config.domain}/bespoke-test/0/1/${JSON.stringify(healthCategoriesPlusTest)}${nativeAppExternalUrl}`
        : `https://build.${window.config.domain}/bespoke-test/0/1/${JSON.stringify(healthCategoriesPlusTest)}${nativeAppExternalUrl}`,
    };
  }, [
    kitBaseCost,
    websiteURL,
    plusTests,
    healthCategories,
    userDetails?.customerFirstName,
    userDetails?.healthCategoriesPreferences,
  ]);

  const getBespokeTestFromOutRange = useCallback(
    (params = {}) => {
      const {orderId} = params;
      let markersOutOfRange = [];
      let testName = "";

      if (orderId) {
        const result = getResultByOrderId({orderId});

        const isAntibodyTest = result?.antibodyResultText !== "";
        const isFemaleTest = result?.isFemaleHormoneTest != "0";
        const isSingleMarkerTest = result?.totalResults == 1;

        if (!isAntibodyTest && !isFemaleTest && !isSingleMarkerTest) {
          testName = result?.testName || "";
          markersOutOfRange =
            result?.biomarkers
              ?.filter(biomarker => biomarker.rangeStatus != "Normal")
              ?.map(marker => marker?.biomarkerId) || [];
        }
      } else {
        const isAntibodyTest = latestResult?.antibodyResultText !== "";
        const isFemaleTest = latestResult?.isFemaleHormoneTest != "0";
        const isSingleMarkerTest = latestResult?.totalResults == 1;

        if (!isAntibodyTest && !isFemaleTest && !isSingleMarkerTest) {
          testName = latestResult?.testName || "";
          markersOutOfRange =
            latestResult?.biomarkers
              ?.filter(biomarker => biomarker.rangeStatus != "Normal")
              ?.map(marker => marker?.biomarkerId) || [];
        }
      }

      if (isEmpty(markersOutOfRange)) return {};

      let bespokeTestOutOfRange = [];
      markersOutOfRange?.forEach(markerId => {
        const plusTestFound =
          [...plusTests]
            .sort((a, b) => {
              if (Array.isArray(a?.biomarkers)) {
                return a.biomarkers.length - b.biomarkers.length;
              }
              return Object.values(a?.biomarkers || {}).length - Object.values(b?.biomarkers || {}).length;
            })
            .find(plusTest => {
              if (Array.isArray(plusTest?.biomarkers)) {
                return plusTest?.biomarkers?.find(biomarker => biomarker.biomarkerId == markerId);
              }
              return Object.values(plusTest?.biomarkers || {})?.find(biomarker => biomarker.biomarkerId == markerId);
            }) || {};

        if (!isEmpty(plusTestFound)) {
          return bespokeTestOutOfRange.push(plusTestFound);
        }
      });
      bespokeTestOutOfRange = uniqBy(bespokeTestOutOfRange, "testId");

      let outOfRangeBiomarkers =
        bespokeTestOutOfRange?.map(test => Object.values(test?.biomarkers || {}))?.flat() || [];
      outOfRangeBiomarkers = uniqBy(outOfRangeBiomarkers, "biomarkerId");
      const testPrice = bespokeTestOutOfRange.reduce((acc, curr) => {
        const testPrice = parseFloat(curr?.testPrice) || 0;
        return acc + testPrice;
      }, 0);

      const healthCategoriesList = getHealthCategories(outOfRangeBiomarkers?.map(biomarker => biomarker?.biomarkerId));
      const plusTestsAdded = [...new Set(outOfRangeBiomarkers.map(test => Number(test.testId)))];

      let nativeAppExternalUrl = "";
      if (window.config.isNativeApp) {
        nativeAppExternalUrl += "?external=true";
      }

      return {
        testName: `${testName?.trim()}'s out of range`,
        biomarkers: outOfRangeBiomarkers || [],
        isSubscriptionTest: 0,
        isBespokeOutRange: 1,
        isBespokeHealthCategories: 0,
        healthCategories: uniqBy(healthCategoriesList, "id") || [],
        testIcon: "bespoke-test-icon",
        testPrice: (testPrice + kitBaseCost).toString(),
        plusTests: plusTestsAdded,
        url: window.config.isDev
          ? `https://devbuild.${window.config.domain}/bespoke-test/0/1/${JSON.stringify(plusTestsAdded)}${nativeAppExternalUrl}`
          : `https://build.${window.config.domain}/bespoke-test/0/1/${JSON.stringify(plusTestsAdded)}${nativeAppExternalUrl}`,
      };
    },
    [
      kitBaseCost,
      websiteURL,
      plusTests,
      healthCategories,
      getResultByOrderId,
      latestResult?.testName,
      latestResult?.biomarkers,
    ],
  );

  const getNextSubscription = useCallback(() => {
    const subscriptionOrders = [
      ...Object.values(subscriptions?.free ?? {}),
      ...Object.values(subscriptions?.paypal ?? {}),
      ...Object.values(subscriptions?.stripe ?? {}),
    ];

    const nextSubscription =
      subscriptionOrders
        ?.filter(subscription => dayjs(subscription?.nextDispatch).diff(dayjs(), "days") >= 0)
        ?.sort((a, b) => b.nextDispatch - a.nextDispatch)
        ?.reverse()
        ?.find(subscription => subscription?.status === "Active") || {};

    let nextTest = nextSubscription || {};

    if (!isEmpty(nextTest)) {
      nextTest = {...nextTest, isSubscriptionTest: 1, isBespokeOutRange: 0, isBespokeHealthCategories: 0};

      const healthCategoriesList = getHealthCategories(nextTest?.biomarkers?.map(biomarker => biomarker?.biomarkerId));

      const testIcon = nextTest?.bespokeTest
        ? "bespoke-test-icon"
        : testList.find(test => test?.testId == nextTest?.testId)?.testIcon;

      nextTest = {...nextTest, testIcon, healthCategories: uniqBy(healthCategoriesList, "id") || []};
    }

    return nextTest;
  }, [subscriptions, testList, latestResult?.testId, healthCategories]);

  const getTestRecommended = useCallback(() => {
    if (isEmpty(testList)) return [];

    if (config.websiteId == 1) {
      const healthCategoryBespokeTest = getBespokeTestFromHealthCategories();
      const outOfRangeBespokeTest = getBespokeTestFromOutRange();
      const nextSubscription = getNextSubscription();
      const baselineHealthTest = testList.find(test => test?.testId == "588525") || {};
      const advancedHealthTest = testList.find(test => test?.testId == "588766") || {};
      const myFormTest = testList.find(test => test?.testId == "237545") || {};
      const testRecommended = [];
      if (!isEmpty(healthCategoryBespokeTest)) {
        testRecommended.push(healthCategoryBespokeTest);
      }
      if (!isEmpty(outOfRangeBespokeTest)) {
        testRecommended.push(outOfRangeBespokeTest);
      }
      if (!isEmpty(nextSubscription)) {
        testRecommended.push(nextSubscription);
      }
      if (testRecommended.length == 1) {
        if (!isEmpty(baselineHealthTest)) {
          testRecommended.push({
            ...baselineHealthTest,
            isExtraTest: 1,
            healthCategories: getHealthCategories(
              baselineHealthTest?.biomarkers?.map(biomarker => biomarker?.biomarkerId),
            ),
            biomarkers: baselineHealthTest?.biomarkers?.sort((a, b) => {
              return a?.biomarkerName?.trim()?.localeCompare(b?.biomarkerName?.trim());
            }),
          });
        }
        if (!isEmpty(advancedHealthTest)) {
          testRecommended.push({
            ...advancedHealthTest,
            isExtraTest: 1,
            healthCategories: getHealthCategories(
              advancedHealthTest?.biomarkers?.map(biomarker => biomarker?.biomarkerId),
            ),
            biomarkers: baselineHealthTest?.biomarkers?.sort((a, b) => {
              return a?.biomarkerName?.trim()?.localeCompare(b?.biomarkerName?.trim());
            }),
          });
        }
        if (!isEmpty(myFormTest) && userDetails?.customerSexId == 2 && userDetails?.customerAge >= 40) {
          testRecommended.push({
            ...myFormTest,
            isExtraTest: 1,
            healthCategories: getHealthCategories(myFormTest?.biomarkers?.map(biomarker => biomarker?.biomarkerId)),
            biomarkers: baselineHealthTest?.biomarkers?.sort((a, b) => {
              return a?.biomarkerName?.trim()?.localeCompare(b?.biomarkerName?.trim());
            }),
          });
        }
      }
      return testRecommended;
    } else if (config.websiteId == 2) {
      const nextSubscription = getNextSubscription();
      let runnerTest = {};
      let cylistsTest = {};
      let triatheleteTest = {};
      let weightliftersTest = {};
      let crossfittersTest = {};
      let myFormTest = {};
      if (userDetails?.customerSexId == 1) {
        runnerTest = testList.find(test => test?.testId == "598850") || {};
        cylistsTest = testList.find(test => test?.testId == "598865") || {};
        triatheleteTest = testList.find(test => test?.testId == "598847") || {};
        weightliftersTest = testList.find(test => test?.testId == "598825") || {};
        crossfittersTest = testList.find(test => test?.testId == "445416") || {};
      } else if (userDetails?.customerSexId == 2) {
        runnerTest = testList.find(test => test?.testId == "598848") || {};
        cylistsTest = testList.find(test => test?.testId == "598863") || {};
        triatheleteTest = testList.find(test => test?.testId == "598843") || {};
        weightliftersTest = testList.find(test => test?.testId == "598823") || {};
        crossfittersTest = testList.find(test => test?.testId == "598827") || {};
        myFormTest = testList.find(test => test?.testId == "549145") || {};
      }
      const testRecommended = [];
      if (!isEmpty(nextSubscription)) {
        testRecommended.push(nextSubscription);
      }
      if (!isEmpty(myFormTest)) {
        testRecommended.push({
          ...myFormTest,
          isExtraTest: 1,
          healthCategories: getHealthCategories(myFormTest?.biomarkers?.map(biomarker => biomarker?.biomarkerId)),
          biomarkers: [...(myFormTest?.biomarkers || [])].sort((a, b) => {
            return a?.biomarkerName?.trim()?.localeCompare(b?.biomarkerName?.trim());
          }),
        });
      }
      if (testRecommended.length == 0) {
        if (!isEmpty(runnerTest)) {
          testRecommended.push({
            ...runnerTest,
            isExtraTest: 1,
            healthCategories: getHealthCategories(runnerTest?.biomarkers?.map(biomarker => biomarker?.biomarkerId)),
            biomarkers: [...(runnerTest?.biomarkers || [])].sort((a, b) => {
              return a?.biomarkerName?.trim()?.localeCompare(b?.biomarkerName?.trim());
            }),
          });
        }
        if (!isEmpty(cylistsTest)) {
          testRecommended.push({
            ...cylistsTest,
            isExtraTest: 1,
            healthCategories: getHealthCategories(cylistsTest?.biomarkers?.map(biomarker => biomarker?.biomarkerId)),
            biomarkers: [...(cylistsTest?.biomarkers || [])].sort((a, b) => {
              return a?.biomarkerName?.trim()?.localeCompare(b?.biomarkerName?.trim());
            }),
          });
        }
        if (!isEmpty(triatheleteTest)) {
          testRecommended.push({
            ...triatheleteTest,
            isExtraTest: 1,
            healthCategories: getHealthCategories(
              triatheleteTest?.biomarkers?.map(biomarker => biomarker?.biomarkerId),
            ),
            biomarkers: [...(triatheleteTest?.biomarkers || [])].sort((a, b) => {
              return a?.biomarkerName?.trim()?.localeCompare(b?.biomarkerName?.trim());
            }),
          });
        }
        if (!isEmpty(weightliftersTest)) {
          testRecommended.push({
            ...weightliftersTest,
            isExtraTest: 1,
            healthCategories: getHealthCategories(
              weightliftersTest?.biomarkers?.map(biomarker => biomarker?.biomarkerId),
            ),
            biomarkers: [...weightliftersTest?.biomarkers].sort((a, b) => {
              return a?.biomarkerName?.trim()?.localeCompare(b?.biomarkerName?.trim());
            }),
          });
        }
        if (!isEmpty(crossfittersTest)) {
          testRecommended.push({
            ...crossfittersTest,
            isExtraTest: 1,
            healthCategories: getHealthCategories(
              crossfittersTest?.biomarkers?.map(biomarker => biomarker?.biomarkerId),
            ),
            biomarkers: [...(crossfittersTest?.biomarkers || [])]?.sort((a, b) => {
              return a?.biomarkerName?.trim()?.localeCompare(b?.biomarkerName?.trim());
            }),
          });
        }
      }

      return testRecommended;
    }
    // return testRecommended;
  }, [getBespokeTestFromHealthCategories, getBespokeTestFromOutRange, getNextSubscription]);

  const getNextTest = useCallback(
    (params = {}) => {
      const {orderId} = params;

      const outOfRangeBespokeTest = getBespokeTestFromOutRange({orderId});
      const sameTest = getSameTest({orderId});

      const testRecommended = [];

      if (!isEmpty(sameTest)) {
        testRecommended.push(sameTest);
      }

      if (!isEmpty(outOfRangeBespokeTest)) {
        testRecommended.push(outOfRangeBespokeTest);
      }

      return testRecommended;
    },
    [getBespokeTestFromOutRange, getSameTest],
  );

  const getHealthCategories = useCallback(
    biomarkers => {
      if (isEmpty(biomarkers)) return [];

      if (!Array.isArray(biomarkers)) {
        return [];
      }

      const healthCategoriesList = healthCategories?.filter(category => {
        return category?.biomarkerIds?.every(biomarkerId => biomarkers?.includes(biomarkerId));
      });

      return healthCategoriesList || [];
    },
    [healthCategories],
  );

  const getClientSecret = useCallback(() => {
    setIsLoading(true);
    const payload = {
      amount: parseFloat(amount) * 100,
      orderReference: orderReference ?? orderFiltered?.orderReference ?? "",
    };

    dispatch(loadClientSecret(payload))
      .then(({payload: response}) => {
        const {client_secret, id, currency, amount} = response;

        setClientSecret(client_secret);
      })
      .finally(() => setIsLoading(false));
  }, [amount, dispatch, orderFiltered?.orderReference, orderReference]);

  const loadRequestReplacementKit = useCallback(
    (params = {}) => {
      const {paymentIntentId, amount} = params;
      const payload = {
        orderId: orderFiltered.orderId,
        orderReference: orderFiltered.orderReference,
        paymentIntentId,
      };

      return dispatch(requestReplacementKit(payload));
    },
    [dispatch, orderFiltered?.orderId, orderFiltered?.orderReference],
  );

  const changeAppointmentType = useCallback(
    (params = {}) => {
      const {paymentIntentId, postPKit} = params;

      const payload = {
        orderId: orderFiltered.orderId,
        orderReference: `${orderFiltered.orderReference}${getAppointmentRef(postPKit)}`,
        stripeToken: {id: paymentIntentId},
        postPKit,
        websiteId: config.websiteId,
      };

      return dispatch(requestChangeAppointmentType(payload));
    },
    [orderFiltered, dispatch],
  );

  const getAppointmentRef = useCallback(postPKit => {
    let ref = "";
    if (postPKit === "Partner Clinic") ref = "_pa";
    if (postPKit === "Kit Only") ref = "_pk";
    if (postPKit === "Appointment") ref = "_pa";

    return ref || "";
  }, []);

  const getCompletedOrders = useCallback(() => {
    if (isEmpty(orders)) return [];

    return orders?.filter(order => ORDER_LAB_STATUS?.completed.some(status => status == order?.orderLabStatus));
  }, [orders]);

  const getCancelledOrders = useCallback(() => {
    if (isEmpty(orders)) return [];

    return orders?.filter(order => ORDER_LAB_STATUS?.cancelled.some(status => status == order?.orderLabStatus));
  }, [orders]);

  const getActiveOrders = useCallback(() => {
    if (isEmpty(orders)) return [];

    const cancelledStatus = ORDER_LAB_STATUS?.cancelled;
    const completedStatus = ORDER_LAB_STATUS?.completed;
    const allCancelledStatus = [...cancelledStatus, ...completedStatus];

    return orders?.filter(order => !allCancelledStatus.some(status => status == order?.orderLabStatus));
  }, [orders]);

  const fetchOrderById = useCallback(async orderId => {
    return dispatch(getOrderById(orderId));
  }, []);

  return {
    orders,
    isFetching,
    orderFiltered,
    clientSecret,
    isLoading,
    fetchOrderById,
    getClientSecret,
    getFemaleOrders,
    getOrderStatusCopy,
    checkOrderIdExist,
    isValidOrderId,
    getOrdersByLabStatus,
    getNextTest,
    getNextSubscription,
    getCurrentKitId,
    getTestRecommended,
    loadRequestReplacementKit,
    changeAppointmentType,
    getCompletedOrders,
    getCancelledOrders,
    getActiveOrders,
    getHealthCategories,
  };
};
